import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Form, Input, Modal, Select } from 'antd';

import { api } from '../../api/client';
import { API_ITEM_CARD_COUNTS_URL } from '../../utils';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { InputIconSelect } from '../Input';
import CardMovePreview from './CardMovePreview';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { createSubCollection } from '../../services/subCollections';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { compareByName } from '../../utils/sort';

/**
 * The cards a move of `cardId` takes with it: the card and every card that
 * extends it and belongs to the same collection (keepr-api § Moving cards —
 * "a collection card with its descendant cards owned by the same
 * collection").
 */
export function cardTreeOf(cardId, cards = [], ownerId) {
	const root = normalizeId(cardId);
	const owned = (cards || []).filter(card => normalizeId(card?.collection_id) === normalizeId(ownerId));
	const tree = new Set(root ? [root] : []);
	// Breadth-first down the parentCardId links (cards cannot cycle, but a
	// bad document must not hang the modal: each card is visited once).
	const queue = root ? [root] : [];
	while (queue.length) {
		const current = queue.shift();
		for (const card of owned) {
			const id = normalizeId(card?._id);
			if (id && !tree.has(id) && normalizeId(card?.parentCardId) === current) {
				tree.add(id);
				queue.push(id);
			}
		}
	}
	return tree;
}

/**
 * NEW SUB-COLLECTION… (docs/plans/sub-collections.md §5.6): a name, an icon
 * and color, and the parent's own cards to move in. Before anything is made
 * the modal says what moves — each chosen card and the child cards that go
 * with it, with their records counted across the family (a new
 * sub-collection takes every record of a card it takes in) — and the
 * primary names it: **Create and move 260 items**. The API makes the
 * collection, then moves each card through the card move's own gates and
 * reports each; a refused card leaves the rest moved and is listed.
 */
export default function NewSubCollectionModal({ open, onClose, parent }) {
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	const cardDefinitions = useOptionalCardDefinitions();
	const cards = cardDefinitions?.cards;
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const refreshCards = cardDefinitions?.refresh;
	const parentId = normalizeId(parent?._id);

	const [name, setName] = useState('');
	const [icon, setIcon] = useState(null);
	const [color, setColor] = useState(null);
	const [cardIds, setCardIds] = useState([]);
	const [busy, setBusy] = useState(false);
	const [nameError, setNameError] = useState(null);

	// A card's records across the family, one aggregation: the counts rows
	// are per card AND collection at a parent.
	const [countByCard, setCountByCard] = useState(null);
	useEffect(() => {
		if (!open) return undefined;
		setName(''); setIcon(null); setColor(null); setCardIds([]); setNameError(null);
		if (!parentId) return undefined;
		let live = true;
		api.get(API_ITEM_CARD_COUNTS_URL, { params: { collection_id: parentId } })
			.then(({ data }) => {
				if (!live) return;
				const map = new Map();
				(Array.isArray(data) ? data : []).forEach(row => {
					const id = normalizeId(row?.card_id);
					if (id) map.set(id, (map.get(id) || 0) + (Number(row?.count) || 0));
				});
				setCountByCard(map);
			})
			.catch(() => { if (live) setCountByCard(new Map()); });
		return () => { live = false; };
	}, [open, parentId]);

	// Only a card the parent OWNS can move (a global card belongs to nobody).
	const movable = useMemo(() => (cards || [])
		.filter(card => card?.scope === 'collection' && normalizeId(card?.collection_id) === parentId && card?.status !== 'deleted')
		.sort(compareByName), [cards, parentId]);

	// The chosen cards and everything that goes with them, once each.
	const preview = useMemo(() => {
		const all = new Set();
		cardIds.forEach(id => cardTreeOf(id, cards, parentId).forEach(member => all.add(member)));
		const rows = [...all].map(id => ({
			_id: id,
			name: cardsById[id]?.name || 'Card',
			itemCount: countByCard ? countByCard.get(id) || 0 : null,
		}));
		return { cards: rows, itemTotal: rows.reduce((sum, row) => sum + (Number(row.itemCount) || 0), 0) };
	}, [cardIds, cards, cardsById, parentId, countByCard]);

	// A chosen card that another chosen card already takes with it is not
	// sent twice (its second move would find it already there).
	const roots = useMemo(() => cardIds.filter(id => !cardIds.some(other => (
		other !== id && cardTreeOf(other, cards, parentId).has(id)
	))), [cardIds, cards, parentId]);

	const primaryText = !cardIds.length ? 'Create sub-collection'
		: preview.itemTotal ? `Create and move ${formatCount(preview.itemTotal, 'item')}`
		: `Create and move ${formatCount(preview.cards.length, 'card')}`;

	const submit = async () => {
		const trimmed = name.trim();
		if (!trimmed) { setNameError('Give the sub-collection a name.'); return; }
		setBusy(true);
		try {
			const result = await createSubCollection(parentId, { name: trimmed, icon, color, moveCardIds: roots });
			const newId = normalizeId(result?.insertedId);
			const moved = Array.isArray(result?.moved) ? result.moved : [];
			const refused = moved.filter(entry => entry && entry.moved === false);
			if (moved.length && typeof refreshCards === 'function') refreshCards();
			if (refused.length) {
				modal.warning({
					title: `${trimmed} was made, but ${refused.length === 1 ? 'one card' : `${refused.length} cards`} did not move`,
					content: (
						<ul className="card-move-preview__refusals">
							{refused.map(entry => (
								<li key={normalizeId(entry.cardId)}>
									<strong>{cardsById[normalizeId(entry.cardId)]?.name || 'A card'}</strong>: {entry.message || 'It could not move.'}
								</li>
							))}
						</ul>
					),
					okText: 'OK',
				});
			} else {
				const items = moved.reduce((sum, entry) => sum + (Number(entry?.itemTotal) || 0), 0);
				message.success(moved.length
					? `${trimmed} is ready: ${formatCount(moved.reduce((sum, entry) => sum + (Array.isArray(entry?.cards) ? entry.cards.length : 1), 0), 'card')}${items ? ` and ${formatCount(items, 'item')}` : ''} moved in.`
					: `${trimmed} is ready.`);
			}
			onClose?.();
			if (newId) navigate(`/collections/${newId}/`);
		} catch (error) {
			message.error(apiErrorMessage(error, 'The sub-collection could not be made.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="New sub-collection"
			okText={primaryText}
			onOk={submit}
			okButtonProps={{ loading: busy, disabled: !name.trim() }}
			onCancel={onClose}
			destroyOnHidden
			className="new-sub-collection-modal"
		>
			<RecordIdentity
				kicker="Sub-collection of"
				badge={<RecordBadge kind="collection" record={parent} title={parent?.name || 'Collection'} size="sm" />}
			/>
			<Form layout="vertical" className="new-sub-collection-modal__form">
				<Form.Item
					label="Name"
					required
					validateStatus={nameError ? 'error' : undefined}
					help={nameError || undefined}
				>
					<Input
						autoFocus
						maxLength={100}
						value={name}
						placeholder="e.g. Health"
						onChange={event => { setName(event.target.value); setNameError(null); }}
						onPressEnter={submit}
					/>
				</Form.Item>
				<InputIconSelect
					elementName="icon"
					elementLabel="Icon and color"
					value={icon}
					onChange={(_, value) => setIcon(value)}
					colorElementName="color"
					colorValue={color}
					onColorChange={(_, hex) => setColor(hex)}
					recordKind="collection"
					help="Leave it, and the sub-collection takes the parent collection's color."
				/>
				<Form.Item
					label="Cards to move in"
					extra={movable.length
						? 'Each card moves with the cards that extend it and every record made from them. Leave it empty to start with the parent collection\'s cards only.'
						: 'This collection has no cards of its own to move. The sub-collection can use all of them as they are.'}
				>
					<Select
						mode="multiple"
						allowClear
						disabled={!movable.length}
						placeholder="None — keep every card here"
						value={cardIds}
						onChange={setCardIds}
						optionFilterProp="searchText"
						options={movable.map(card => ({
							value: normalizeId(card._id),
							searchText: card.name,
							label: <RecordBadge kind="card" record={card} title={card.name} size="xs" />,
						}))}
						aria-label="Cards to move in"
					/>
				</Form.Item>
			</Form>
			{cardIds.length ? (
				<CardMovePreview preview={preview} cardsById={cardsById} sourceId={parentId} />
			) : null}
		</Modal>
	);
}
